<template>
    <section v-if="strengthsRisks" class="strength-risk">
        <h2 class="strength-risk__heading">
            <button
                type="button"
                class="strength-risk__header"
                :aria-expanded="isOpen"
                aria-controls="strength-risk-content"
                @click="isOpen = !isOpen"
            >
                <span id="strength-risk-title" class="strength-risk__title">{{ strengthsRisks.title }}</span>
                <i class="mdi" :class="isOpen ? 'mdi-chevron-down' : 'mdi-chevron-right'" aria-hidden="true"></i>
            </button>
        </h2>

        <BondV2SignupBlurGate
            v-if="isOpen"
            id="strength-risk-content"
            :message="strengthRiskGateMessage"
            @signup-required="handleSignupGate"
        >
            <div class="strength-risk__body">
                <div v-if="strengthsRisks.strengths?.items.length" class="strength-risk__panel">
                    <div class="strength-risk__panel-header">
                        <h3 class="strength-risk__panel-title">{{ strengthsRisks.strengths.title }}</h3>
                    </div>
                    <div
                        class="strength-risk__cards strength-risk__cards--strengths"
                        :class="cardCountClass(strengthsRisks.strengths.items)"
                    >
                        <article
                            v-for="(item, index) in strengths"
                            :key="`strength-${index}-${item.title}`"
                            class="signal-card signal-card--positive"
                        >
                            <h4 class="signal-card__title">
                                <span v-if="item.iconUrl" class="signal-card__icon" aria-hidden="true">
                                    <img :src="item.iconUrl" alt="" />
                                </span>
                                <strong>{{ item.title }}</strong>
                            </h4>
                            <p>{{ item.description }}</p>
                        </article>
                    </div>
                </div>

                <div v-if="strengthsRisks.risks?.items.length" class="strength-risk__panel">
                    <div class="strength-risk__panel-header">
                        <h3 class="strength-risk__panel-title">{{ strengthsRisks.risks.title }}</h3>
                    </div>
                    <div
                        class="strength-risk__cards strength-risk__cards--risks"
                        :class="cardCountClass(strengthsRisks.risks.items)"
                    >
                        <article
                            v-for="(item, index) in risks"
                            :key="`risk-${index}-${item.title}`"
                            class="signal-card signal-card--negative"
                        >
                            <h4 class="signal-card__title">
                                <span v-if="item.iconUrl" class="signal-card__icon" aria-hidden="true">
                                    <img :src="item.iconUrl" alt="" />
                                </span>
                                <strong>{{ item.title }}</strong>
                            </h4>
                            <p>{{ item.description }}</p>
                        </article>
                    </div>
                </div>
            </div>
        </BondV2SignupBlurGate>
        <p v-if="isOpen" class="strength-risk__disclaimer">
            Summarised from the issuer's disclosures and the rating rationale. Read the full offer document and rating rationale before investing.
        </p>
    </section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import BondV2SignupBlurGate from './BondV2SignupBlurGate.vue'
import type { BondV2StrengthRiskItem } from './bondV2Data'
import { useBondV2Data } from './bondV2Context'

const isOpen = ref(true)
const data = useBondV2Data()
const emit = defineEmits<{
    'signup-required': [payload: SignupGatePayload]
}>()

interface SignupGatePayload {
    title: string
    message: string
    ctaName: string
}

const strengthsRisks = computed(() => data.value.strengthsRisks)
const strengths = computed(() => strengthsRisks.value?.strengths?.items ?? [])
const risks = computed(() => strengthsRisks.value?.risks?.items ?? [])
const strengthRiskGateMessage = computed(() => `Sign up to view ${strengthsRisks.value?.title || 'Strengths & Risk'}`)

const cardCountClass = (items: BondV2StrengthRiskItem[]) => {
    const count = Math.max(1, Math.min(6, items.length))
    return `strength-risk__cards--count-${count}`
}

const handleSignupGate = () => {
    const sectionTitle = strengthsRisks.value?.title || 'Strengths & Risk'
    emit('signup-required', {
        title: `Sign Up to View ${sectionTitle}`,
        message: `Sign up to access ${sectionTitle} and issuer risk insights.`,
        ctaName: 'signup-strength-risk-bond-details'
    })
}
</script>

<style scoped>
.strength-risk {
    border: none;
    border-radius: var(--bond-v2-radius, 7px);
    background: #F9FBFF;
    overflow: hidden;
    padding: 12px;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
}

.strength-risk__disclaimer {
    font-size: 11px;
    color: var(--color-text-secondary, #4d5d7c);
    line-height: 16px;
    margin: 16px 0 0;
    font-style: italic;
}

.strength-risk__header {
    width: 100%;
    border: none;
    background: #F9FBFF;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 0;
    cursor: pointer;
}

.strength-risk__heading {
    margin: 0;
}

.strength-risk__title {
    margin: 0;
    color: var(--bond-v2-text, var(--color-text-primary));
    font-size: 15px;
    font-weight: 800;
}

.strength-risk__header i {
    color: var(--bond-v2-primary, var(--color-primary-40));
    font-size: 18px;
}

.strength-risk__body {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding-top: 16px;
}

.strength-risk__panel {
    background: #fff;
    border-radius: 7px;
    padding: 16px 12px;
}

.strength-risk__panel-header {
    min-height: 18px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 14px;
}

.strength-risk__panel-title {
    margin: 0;
    color: #1b2559;
    font-family: Inter, var(--font-family-primary);
    font-size: 13px;
    font-style: normal;
    font-weight: 700;
    line-height: 18px;
}

.strength-risk__cards {
    display: grid;
    gap: 12px;
}

.strength-risk__cards--strengths,
.strength-risk__cards--risks {
    grid-template-columns: repeat(6, minmax(0, 1fr));
}

.strength-risk__cards--count-1 .signal-card {
    grid-column: span 6;
}

.strength-risk__cards--count-2 .signal-card,
.strength-risk__cards--count-4 .signal-card,
.strength-risk__cards--count-5 .signal-card:nth-child(n + 4) {
    grid-column: span 3;
}

.strength-risk__cards--count-3 .signal-card,
.strength-risk__cards--count-5 .signal-card:nth-child(-n + 3),
.strength-risk__cards--count-6 .signal-card {
    grid-column: span 2;
}

.signal-card {
    min-height: 93px;
    border-radius: 6px;
    padding: 16px 12px;
    box-sizing: border-box;
}

.signal-card--positive {
    border: 1px solid #0f6b32;
    background: #eaffdf;
}

.signal-card--negative {
    border: 1px solid #a31616;
    background: #ffe3e3;
}

.signal-card__title {
    display: flex;
    align-items: flex-start;
    gap: 4px;
    margin: 0;
}

.signal-card strong {
    display: block;
    color: #0f6b32;
    font-family: Inter, var(--font-family-primary);
    font-size: 15px;
    font-style: normal;
    line-height: 21px;
    font-weight: 900;
    white-space: nowrap;
}

.signal-card--negative strong {
    color: #a31616;
}

.signal-card__icon {
    width: 21px;
    height: 21px;
    border-radius: 300px;
    background: #eaf7ff;
    color: #0f6b32;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 21px;
}

.signal-card--negative .signal-card__icon {
    color: #a31616;
}

.signal-card__icon img {
    width: 17px;
    height: 17px;
    display: block;
    object-fit: contain;
}

.signal-card p {
    margin: 4px 0 0;
    color: #1b2559;
    font-family: Inter, var(--font-family-primary);
    font-size: 11px;
    font-style: normal;
    line-height: 18px;
    font-weight: 400;
}

@media (max-width: 900px) {
    .strength-risk__cards,
    .strength-risk__cards--strengths,
    .strength-risk__cards--risks {
        grid-template-columns: 1fr;
        width: 100%;
        justify-items: stretch;
    }

    .strength-risk__cards--strengths .signal-card,
    .strength-risk__cards--risks .signal-card,
    .strength-risk__cards--strengths .signal-card:nth-child(n),
    .strength-risk__cards--risks .signal-card:nth-child(n) {
        grid-column: 1 / -1;
        width: 100%;
        max-width: 100%;
    }
}

@media (max-width: 560px) {
    .strength-risk {
        border-radius: 0;
        padding: 12px 16px;
        box-sizing: border-box;
    }

    .strength-risk__panel {
        padding: 14px 12px;
    }

    .strength-risk__title {
        font-size: 14px;
    }

    .strength-risk__panel-header {
        gap: 8px;
        margin-bottom: 12px;
    }

    .signal-card strong {
        font-size: 14px;
        line-height: 20px;
        white-space: normal;
    }

    .signal-card {
        width: 100%;
        max-width: 100%;
        min-height: 0;
        padding: 14px 10px;
        box-sizing: border-box;
    }
}
</style>
